import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { CampaignAutomationLeadParams } from "@/types/campaign-pipeline-automation";
import { fetchCampaignAutomationLeads, fetchCampaignPipelineAutomation } from "./campaign-pipeline-automation";
export const campaignPipelineAutomationKeys = {
  all: ["campaign-pipeline-automation"] as const,
  summaries: (campaignId: string) => [...campaignPipelineAutomationKeys.all, campaignId, "summary"] as const,
  summary: (campaignId: string, pipelineId?: string) => [...campaignPipelineAutomationKeys.summaries(campaignId), pipelineId ?? null] as const,
  leads: (campaignId: string, params: CampaignAutomationLeadParams) => [...campaignPipelineAutomationKeys.all, campaignId, "leads", params] as const
};

/**
 * Whether the previous page of rows still describes what is on screen.
 *
 * Paging, searching and slicing keep the table up instead of collapsing to a
 * skeleton on every keystroke. A stage or step change is different: those rows
 * belong to somewhere else, and sitting them under the new heading reads as
 * fact rather than as staleness.
 */
export function isSameLeadSlice(previous: CampaignAutomationLeadParams | undefined, next: CampaignAutomationLeadParams): boolean {
  if (!previous) return false;
  return previous.stageId === next.stageId && previous.stepId === next.stepId;
}
export function useCampaignPipelineAutomation(campaignId, pipelineId) {
  const $ = _c(10);
  let t0;
  if ($[0] !== campaignId || $[1] !== pipelineId) {
    t0 = campaignPipelineAutomationKeys.summary(campaignId, pipelineId);
    $[0] = campaignId;
    $[1] = pipelineId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== campaignId || $[4] !== pipelineId) {
    t1 = () => fetchCampaignPipelineAutomation(campaignId, pipelineId);
    $[3] = campaignId;
    $[4] = pipelineId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!campaignId;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      placeholderData: keepPreviousData
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function useCampaignAutomationLeads(campaignId, params, t0) {
  const $ = _c(13);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== campaignId || $[1] !== params) {
    t1 = campaignPipelineAutomationKeys.leads(campaignId, params);
    $[0] = campaignId;
    $[1] = params;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== campaignId || $[4] !== params) {
    t2 = () => fetchCampaignAutomationLeads(campaignId, params);
    $[3] = campaignId;
    $[4] = params;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = enabled && !!campaignId;
  let t4;
  if ($[6] !== params) {
    t4 = (previous, previousQuery) => isSameLeadSlice(previousQuery?.queryKey[3] as CampaignAutomationLeadParams | undefined, params) ? previous : undefined;
    $[6] = params;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      placeholderData: t4
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return useQuery(t5);
}